import { useCallback, useEffect, useRef } from 'react';

import { DraftEmailType, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { resolveLoadedDraftVersion } from './resolveLoadedDraftVersion';
import { DraftProbeStatus } from '../../../../../enums';
import { probeDraftVersion } from '../../../../../hooks/UserCommunications/draft/probeDraftVersion';
import { resolveDraftAccount } from '../../../../../hooks/UserCommunications/draft/resolveEmailAccount';
import { useIntegrationsStore } from '../../../../../store';
import { fastForwardDraftVersion } from '../../../../../utils/draftWrites';

const LOG_MESSAGES = {
  PROBE_NOT_FOUND: '[Draft] probe: the draft no longer exists on the provider',
  PROBE_UNKNOWN:
    '[Draft] probe could not read the draft version; first save is unguarded',
} as const;

interface UseLoadedDraftVersionProps {
  draftId?: string;
  userCommunication?: UserCommunication;
  draftType?: DraftEmailType;
  seedDraftVersionIfAbsent: (version: string) => void;
  onDraftNotFound?: () => void;
}

// Seeds the version of the content the compose loaded: from the row when it
// carries one, else a probe that runs beside the open. The first save awaits it.
export const useLoadedDraftVersion = ({
  draftId,
  userCommunication,
  draftType,
  seedDraftVersionIfAbsent,
  onDraftNotFound,
}: UseLoadedDraftVersionProps) => {
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore();
  const resolvedForDraftIdRef = useRef<string | undefined>(undefined);
  const pendingProbeRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!draftId || resolvedForDraftIdRef.current === draftId) {
      return;
    }
    const rowVersion = resolveLoadedDraftVersion(userCommunication);
    if (rowVersion) {
      resolvedForDraftIdRef.current = draftId;
      seedDraftVersionIfAbsent(
        fastForwardDraftVersion(draftId, rowVersion) ?? rowVersion,
      );
      return;
    }
    if (!currentIntegrationAccount) {
      return;
    }
    resolvedForDraftIdRef.current = draftId;
    const account = resolveDraftAccount(
      userCommunication?.userEmailAddress,
      currentIntegrationAccount,
      integrationAccounts ?? [],
      { strict: true },
    );
    const probe = probeDraftVersion({ draftId, account, draftType })
      .then((result) => {
        if (result.status === DraftProbeStatus.EXISTS) {
          seedDraftVersionIfAbsent(
            fastForwardDraftVersion(draftId, result.draftVersion) ??
              result.draftVersion,
          );
        } else if (result.status === DraftProbeStatus.NOT_FOUND) {
          log.debug(LOG_MESSAGES.PROBE_NOT_FOUND, { draftId });
          onDraftNotFound?.();
        } else {
          log.warn(LOG_MESSAGES.PROBE_UNKNOWN, { draftId });
        }
      })
      .catch(() => log.warn(LOG_MESSAGES.PROBE_UNKNOWN, { draftId }))
      .finally(() => {
        if (pendingProbeRef.current === probe) {
          pendingProbeRef.current = null;
        }
      });
    pendingProbeRef.current = probe;
  }, [
    draftId,
    userCommunication,
    draftType,
    currentIntegrationAccount,
    integrationAccounts,
    seedDraftVersionIfAbsent,
    onDraftNotFound,
  ]);

  const awaitVersionProbe = useCallback(
    (): Promise<void> => pendingProbeRef.current ?? Promise.resolve(),
    [],
  );

  return { awaitVersionProbe };
};
